import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import { SHIFT_AND_REPORTS_VIEW_PERMISSION } from '@/constants/permissions'
import { getFirstDivisionInfo } from '@/core/auth/utils'
import { SHIFT_NOTIFICATION_TYPES } from '@/modules/shift-and-reports/constants/notifications'
import router from '@/router'
import store from '@/store'
import { notificationService } from '@/utils/notifications'
import useFormat from '@/views/utils'

export function useShiftNotifications() {
    const timeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
    }

    const { user, hasPermission } = useAuth()
    const { connect, subscribe } = useCentrifugo()
    const { formatOnlyTime } = useFormat()

    /**
     * Обработчик входящих уведомлений по сменам
     * @param {object} data - Данные, пришедшие от Centrifugo
     * @param {string} divisionId - Подразделение канала, из которого пришло событие
     */
    const handlePublication = (data, divisionId) => {
        // Работаем только с нужным типом событий
        if (
            !data.type ||
            !Object.values(SHIFT_NOTIFICATION_TYPES).includes(data.type)
        ) {
            return
        }

        // Экран «Смена и отчёты» показывает первое подразделение пользователя —
        // события остальных подразделений не должны менять его состояние
        if (divisionId !== getFirstDivisionInfo().id) {
            return
        }

        switch (data.type) {
            case SHIFT_NOTIFICATION_TYPES.CLOSING:
            case SHIFT_NOTIFICATION_TYPES.CREATED:
                store.commit('shiftAndReports/SET_SHIFT_PROCESSING', true)
                break
            case SHIFT_NOTIFICATION_TYPES.OPENED:
            case SHIFT_NOTIFICATION_TYPES.CLOSED: {
                store.commit('shiftAndReports/SET_CURRENT_SHIFT', data)
                store.commit('shiftAndReports/SET_SHIFT_PROCESSING', false)

                const date =
                    data.type === SHIFT_NOTIFICATION_TYPES.OPENED
                        ? data.opened_at
                        : data.closed_at || new Date().toISOString()

                const statusText =
                    data.type === SHIFT_NOTIFICATION_TYPES.OPENED
                        ? 'Смена успешно открыта в'
                        : 'Смена успешно закрыта в'

                notificationService.showSuccess(
                    `${statusText} ${formatOnlyTime(date, timeFormatOptions)}`
                )
                break
            }
            case SHIFT_NOTIFICATION_TYPES.ERROR:
            case SHIFT_NOTIFICATION_TYPES.CLOSE_FAILED: {
                if (router.currentRoute.name === 'ShiftAndReports') {
                    store.commit(
                        'shiftAndReports/SET_IS_R_KEEPER_HAS_ERROR',
                        true
                    )
                    store.commit(
                        'shiftAndReports/SET_R_KEEPER_ERROR_TEXT',
                        data.error
                    )
                } else {
                    notificationService.showError(
                        null,
                        'Произошла ошибка при открытии/закрытии смены.\n' +
                            'Обратитесь в техподдержку'
                    )
                }

                store.commit('shiftAndReports/SET_SHIFT_PROCESSING', false)
                break
            }
            default:
                if (import.meta.env.DEV) {
                    console.debug(
                        '[WS: Shift] Необработанный тип события:',
                        data.type
                    )
                }
                break
        }
    }

    /**
     * Инициализирует подключение и подписки на каналы стоп-листов
     */
    const init = () => {
        if (!user) return

        const { divisions } = user.value
        const hasDivisions = divisions && divisions.length > 0

        if (
            !hasDivisions ||
            !hasPermission(SHIFT_AND_REPORTS_VIEW_PERMISSION)
        ) {
            return
        }

        connect()

        divisions.forEach(division => {
            if (division.id) {
                const channel = `eds:shift:${division.id}`
                subscribe(channel, data => handlePublication(data, division.id))
            }
        })
    }

    return {
        initShiftNotifications: init,
    }
}
